/**
 * What the storefront may say about ONE package's supplier-backed facts and its start — read from the
 * claim matrix.
 *
 * WHY THIS EXISTS (BUG-CAT-04, ADR-005 §1, ADR-006 Q3)
 * ---------------------------------------------------
 * Origin, quality and start statements may only repeat what the bound supplier's own text supports,
 * and fulfilment is manual: an operator reviews every order and the honest start is "after operator
 * review, usually within 24 hours".
 *
 * REFILL IS NOT DECIDED HERE ANY MORE (G03, owner decisions D-06/D-07/D-08, 2026-10-02)
 * -------------------------------------------------------------------------------------
 * Until G03 this file carried `refillTermsForGroup()`, which promised a refill only where the group's
 * supplier text said "N Gün Telafili" and printed "No refill (the supplier offers none)" elsewhere
 * (ADR-006 Q21). The owner replaced that rule: every package has a refill that SociShop funds, 30/90/365
 * days by the package's USD list price. The single decider is lib/refill-policy.ts; an order shows the
 * term stamped on it. The matrix's `refillDays` / `noRefill` stay in claim-matrix.generated.ts as
 * SUPPLIER facts (what the supplier itself states — the cost risk of D-08) and no customer surface reads
 * them (lib/__tests__/claim-matrix-generated.test.ts guards that).
 *
 * Data: lib/claims/claim-matrix.generated.ts (projection of
 * SOCISHOP_OBSIDIAN_VAULT/03-Catalog/claim-matrix.json by `scripts/claim-matrix.py emit-ts`, locked by
 * lib/__tests__/claim-matrix-generated.test.ts). Package-id lookups live in ./package-claims-by-id so
 * the ~26 KB id table only ships where a package id is all a surface knows.
 */
import { CLAIM_GROUP_TERMS, type ClaimGroupTerms } from "./claim-matrix.generated";

/** The start target of manual fulfilment (ADR-006 Q3). No page may show a shorter window. */
export const MANUAL_START_HOURS = 24;

const PLATFORM_ALIAS: Record<string, string> = { x: "twitter" };

/**
 * Group id (`platform/service/list/tier`) of a catalogue row — the same resolution as
 * scripts/claim-matrix.py (service_key / geo_of / tier_of) and Go catalogcontract.ResolveServiceKey.
 * Returns null when the slugs do not describe a catalogue group.
 */
export function claimGroupId(
    platform: string | null | undefined,
    categorySlug: string | null | undefined,
    subCategorySlug: string | null | undefined,
): string | null {
    const p = (platform ?? "").trim().toLowerCase();
    const cat = (categorySlug ?? "").trim().toLowerCase();
    const sub = (subCategorySlug ?? "").trim().toLowerCase();
    if (!p || !cat) return null;
    const plat = PLATFORM_ALIAS[p] ?? p;
    if (cat.startsWith("global-")) {
        const raw = cat.slice("global-".length);
        const service = raw === "watchtime" ? "watch-time" : raw;
        if (!service || !sub) return null;
        const tier = sub === "standart" ? "standard" : sub;
        return `${plat}/${service}/global/${tier}`;
    }
    if (cat.startsWith("gercek-")) {
        if (!sub) return null;
        return `${plat}/${sub}/real-${cat.slice("gercek-".length)}/local-profile`;
    }
    return null;
}

export function claimTermsForGroup(groupId: string | null | undefined): ClaimGroupTerms | null {
    if (!groupId) return null;
    return Object.prototype.hasOwnProperty.call(CLAIM_GROUP_TERMS, groupId) ? CLAIM_GROUP_TERMS[groupId] : null;
}

/**
 * Upper bound, in hours, of a catalogue window such as "24 saat", "24-48 saat", "0-2 saat", "30 dk",
 * "2 gün". Same parse as the claim gate's delivery_time test. null = not a window.
 */
export function deliveryWindowUpperHours(value: string | null | undefined): number | null {
    const v = value ?? "";
    const range = /(\d+)\s*[-–]\s*(\d+)\s*(saat|dk|gün)/iu.exec(v);
    const single = /(\d+)\s*(saat|dk|gün)/iu.exec(v);
    const n = range ? Number(range[2]) : single ? Number(single[1]) : null;
    if (n === null || !Number.isFinite(n)) return null;
    const unit = (range ? range[3] : single ? single[2] : "").toLowerCase();
    return unit === "dk" ? n / 60 : unit === "gün" ? n * 24 : n;
}

/**
 * The catalogue window if it can be shown honestly under manual fulfilment, else null.
 *
 * A row still carrying "0-2 saat" (a pre-2026-09-23 snapshot, a cached API answer) must not reach
 * the page: the caller renders the ADR-006 start sentence instead.
 */
export function honestStartWindow(value: string | null | undefined): string | null {
    const hours = deliveryWindowUpperHours(value);
    if (hours === null || hours < MANUAL_START_HOURS) return null;
    return (value ?? "").trim() || null;
}
